import { useEffect, useRef, useState } from 'react'
import Modal from '../UI/Modal'
import Markdown from '../common/Markdown'
import { useT } from '../../hooks/useTranslation'
import { useHaptic } from '../../hooks/useHaptic'
import { useDashboardStore } from '../../store/dashboardStore'
import { getInstruction } from '../../services/api'

// Базовый URL API (совпадает с BASE_URL в src/services/api.js): картинки
// инструкции отдаются из /api/instruction/img/<имя>.
const API_BASE = import.meta.env.VITE_API_URL ?? '/api'

// Полная инструкция пользователя. Контент читается с сервера из markdown-
// файла ПРИ КАЖДОМ открытии — владелец правит файл напрямую, правки видны
// сразу. Открывается из раздела «Настройки» (шестерёнка) → «Инструкция» и
// из карточки-приветствия на Главной.
export default function InstructionModal({ onClose }) {
  const t = useT()
  const haptic = useHaptic()
  const modalRef = useRef(null)
  const language = useDashboardStore(s => s.language)
  const setModal = useDashboardStore(s => s.setActiveModal)

  const [state, setState] = useState({ loading: true, error: null, content: '', updatedAt: null })
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setState(s => ({ ...s, loading: true, error: null }))
    getInstruction(language)
      .then(data => {
        if (cancelled) return
        setState({ loading: false, error: null, content: data?.content || '', updatedAt: data?.updatedAt || null })
      })
      .catch(err => {
        if (cancelled) return
        setState({ loading: false, error: err?.message || String(err), content: '' })
      })
    return () => { cancelled = true }
  }, [language, reloadKey])

  const retry = () => { haptic.light(); setReloadKey(k => k + 1) }
  const close = () => { haptic.light(); modalRef.current?.close() }
  // Кнопка «Как получить ключи на бирже?» (Шаг 2) → модалка с гайдом.
  const openApiKeysGuide = () => { haptic.light(); setModal('api-keys') }

  const footer = (
    <button
      onClick={close}
      style={{
        width: '100%', padding: '14px', borderRadius: 14,
        background: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)',
        color: '#fff', fontSize: 15, fontWeight: 700,
        border: 'none', cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {t.instructionClose}
    </button>
  )

  return (
    <Modal ref={modalRef} title={t.instructionTitle} onClose={onClose} footer={footer}>
      {state.loading ? (
        <div style={{ padding: '4px 0' }}>
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="skeleton-shimmer" style={{
              height: i === 0 ? 26 : 14,
              borderRadius: 8, marginBottom: 14,
              width: i === 0 ? '60%' : i === 1 ? '100%' : i === 2 ? '85%' : '40%',
            }} />
          ))}
        </div>
      ) : state.error ? (
        <div style={{ textAlign: 'center', padding: '36px 20px' }}>
          <div style={{ fontSize: 15, color: '#fff', fontWeight: 600, marginBottom: 6 }}>{t.instructionError}</div>
          <div style={{ fontSize: 12.5, color: '#636366', marginBottom: 18 }}>{state.error}</div>
          <button
            onClick={retry}
            style={{
              padding: '12px 26px', borderRadius: 12,
              background: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)',
              color: '#fff', fontSize: 14, fontWeight: 700,
              border: 'none', cursor: 'pointer',
            }}
          >
            {t.instructionRetry}
          </button>
        </div>
      ) : (
        <div style={{ padding: '2px 0 8px' }}>
          <Markdown text={state.content} baseUrl={`${API_BASE}/instruction`} onApiKeysGuide={openApiKeysGuide} />
          {state.updatedAt && (
            <p style={{ fontSize: 11, color: '#4A4A4D', margin: '18px 0 0', textAlign: 'center' }}>
              {t.instructionUpdated}: {state.updatedAt.slice(0, 10)}
            </p>
          )}
        </div>
      )}
    </Modal>
  )
}
